/**
 * The welcome pack as a product shot — the pack's pieces laid out on a paper
 * surface in shallow 3D, composed per trade.
 *
 * This is a live mockup, not artwork: each piece is the real component fed by
 * the site's own identity, so whatever brand the deployed site wears is what
 * the scene shows. The depth is pure CSS — a perspective stage, per-piece
 * translateZ for parallax, a few degrees of pointer-follow tilt (touch
 * devices get a slow idle drift instead), a shallow depth of field on the
 * far pieces, and pieces that settle onto the table when the scene scrolls
 * into view. Film grain, a vignette and a key light sit on top so the scene
 * reads as a photograph rather than a screenshot. All motion respects
 * prefers-reduced-motion.
 *
 * Scale: pieces keep a believable size ORDER (sign > magnet > hanger > card)
 * without being to true scale — a 24in sign at the card's px/inch would fill
 * the stage. The card is the focal piece and flips to its back on hover.
 *
 * Scenes: a trade that works out of a van (plumbing, HVAC, electrical…) gets
 * the car magnet as the hero and no yard sign; a curb trade whose work is
 * visible from the street (landscaping, roofing…) gets the yard sign and no
 * magnet; anything unrecognised gets the full spread. Dashed blueprint
 * schematics say where each piece lives: a door knob behind the hanger's
 * hole, H-stakes and a ground line under the sign, a van door around the
 * magnet.
 *
 * Two canvases: 1040×560 for wide containers, and a 420×620 portrait
 * composition once the container drops under 560px — invites are opened
 * from SMS, so the phone layout is the one most prospects actually see.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { packAsset } from './assets';
import { CARD_H, CARD_W, CardBack, CardFront, type PackBusiness } from './BusinessCard';
import { PackPlate } from './PackPlate';
import {
  CarMagnet,
  DoorHanger,
  HANGER_H,
  HANGER_W,
  MAGNET_H,
  MAGNET_W,
  SIGN_H,
  SIGN_W,
  YardSign,
} from './PackItems';

const SCENE_W = 1040;
const SCENE_H = 560;
const MOBILE_W = 420;
const MOBILE_H = 620;
/** Below this container width the portrait composition takes over. */
const NARROW_AT = 560;

/* ------------------------------------------------------------------ */
/* Scenes                                                              */
/* ------------------------------------------------------------------ */

type PieceKind = 'sign' | 'magnet' | 'hanger' | 'card';

interface PieceSpec {
  kind: PieceKind;
  x: number;
  y: number;
  /** Height above the surface — drives parallax, shadow depth and focus. */
  z: number;
  rotate: number;
  scale: number;
  /** Idle bob duration in seconds — only the lifted pieces float. */
  float?: number;
}

export type SceneId = 'van' | 'curb' | 'full' | 'duo';

/**
 * Trades that sell door-to-door out of a van: the magnet is the hero, a yard
 * sign would never be planted.
 */
const VAN_TRADES = new Set([
  'plumbing',
  'hvac',
  'electrical',
  'cleaning',
  'refinishing',
  'handyman',
  'appliance repair',
]);

/** Trades whose finished work stands in a yard for the street to see. */
const CURB_TRADES = new Set([
  'landscaping',
  'roofing',
  'fencing',
  'painting',
  'concrete',
  'paving',
  'lawn care',
  'tree service',
]);

export function sceneForTrade(trade: string): SceneId {
  const t = trade.trim().toLowerCase();
  if (VAN_TRADES.has(t)) return 'van';
  if (CURB_TRADES.has(t)) return 'curb';
  return 'full';
}

/** The pack, named — the invite copy lists exactly what its scene shows. */
export const SCENE_PIECES: Record<SceneId, string> = {
  van: 'Cards, door hangers and a car magnet',
  curb: 'Cards, door hangers and a yard sign',
  full: 'Cards, door hangers, a car magnet and a yard sign',
  duo: 'Business cards and a car magnet',
};

/*
 * Piece scales, chosen against each item's own screen PPI so the on-screen
 * size ORDER matches the physical one: card 3.5in stays smallest, hanger 11in
 * stands taller than the card, the 24in formats read biggest.
 */
const COMPOSITIONS: Record<SceneId, PieceSpec[]> = {
  full: [
    { kind: 'sign', x: 30, y: 48, z: 12, rotate: -3, scale: 0.75 },
    { kind: 'magnet', x: 470, y: 40, z: 24, rotate: 2, scale: 0.75 },
    { kind: 'hanger', x: 880, y: 116, z: 48, rotate: 5, scale: 0.62, float: 7 },
    { kind: 'card', x: 410, y: 330, z: 84, rotate: -3, scale: 0.75, float: 9 },
  ],
  van: [
    { kind: 'magnet', x: 80, y: 70, z: 24, rotate: -2, scale: 0.8 },
    { kind: 'hanger', x: 810, y: 100, z: 48, rotate: 5, scale: 0.62, float: 7 },
    { kind: 'card', x: 400, y: 340, z: 84, rotate: -2.5, scale: 0.75, float: 9 },
  ],
  curb: [
    { kind: 'sign', x: 60, y: 56, z: 24, rotate: -2.5, scale: 0.85 },
    { kind: 'hanger', x: 850, y: 110, z: 48, rotate: 4, scale: 0.62, float: 7 },
    { kind: 'card', x: 540, y: 336, z: 84, rotate: -3, scale: 0.75, float: 9 },
  ],
  /* The physical pack, exactly as it ships: the magnet and the card, nothing
     the box does not contain. Both pieces run larger than in the spreads —
     two items must carry the whole stage. */
  duo: [
    { kind: 'magnet', x: 120, y: 100, z: 24, rotate: -2, scale: 0.92 },
    { kind: 'card', x: 596, y: 306, z: 84, rotate: -3, scale: 0.85, float: 9 },
  ],
};

/** Portrait compositions for phones — fewer pieces, all of them readable. */
const MOBILE_COMPOSITIONS: Record<SceneId, PieceSpec[]> = {
  van: [
    { kind: 'magnet', x: 24, y: 44, z: 24, rotate: -2, scale: 0.6 },
    { kind: 'hanger', x: 298, y: 236, z: 48, rotate: 4, scale: 0.46, float: 7 },
    { kind: 'card', x: 14, y: 402, z: 84, rotate: -3, scale: 0.64, float: 9 },
  ],
  curb: [
    { kind: 'sign', x: 22, y: 44, z: 24, rotate: -2.5, scale: 0.62 },
    { kind: 'card', x: 38, y: 400, z: 84, rotate: -3, scale: 0.75, float: 9 },
  ],
  full: [
    { kind: 'sign', x: 22, y: 40, z: 12, rotate: -2.5, scale: 0.55 },
    { kind: 'hanger', x: 314, y: 300, z: 48, rotate: 4, scale: 0.4, float: 7 },
    { kind: 'card', x: 14, y: 412, z: 84, rotate: -3, scale: 0.64, float: 9 },
  ],
  duo: [
    { kind: 'magnet', x: 14, y: 96, z: 24, rotate: -2, scale: 0.68 },
    { kind: 'card', x: 30, y: 348, z: 84, rotate: -3, scale: 0.78, float: 9 },
  ],
};

const PIECE_SIZE: Record<PieceKind, { w: number; h: number }> = {
  sign: { w: SIGN_W, h: SIGN_H },
  magnet: { w: MAGNET_W, h: MAGNET_H },
  hanger: { w: HANGER_W, h: HANGER_H },
  card: { w: CARD_W, h: CARD_H },
};

/* ------------------------------------------------------------------ */
/* Photography                                                         */
/* ------------------------------------------------------------------ */

/** Tiled SVG fractal noise — film grain over the whole shot. */
const GRAIN =
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E` +
  `%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E` +
  `%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E` +
  `%3Crect width='180' height='180' filter='url(%23n)'/%3E%3C/svg%3E")`;

/** Schematic ink — the dashed blueprint annotations. */
const SCHEMA_INK = 'rgba(30,34,40,0.34)';

/**
 * Generated table photos (nano-banana via OpenRouter, one per scene) — a
 * workbench for the van trades, a mown lawn for the curb trades, a linen mat
 * for the rest. Preloaded at runtime; until (or unless) one arrives the stage
 * keeps its gradient, so a missing asset costs nothing but atmosphere.
 */
const SCENE_BG: Record<SceneId, string> = {
  van: '/pack/scene-van.jpg',
  curb: '/pack/scene-curb.jpg',
  full: '/pack/scene-full.jpg',
  // The duo ships on the van surface — the magnet belongs on a workbench.
  duo: '/pack/scene-van.jpg',
};

/**
 * Resolved lazily rather than baked into `SCENE_BG` at module scope: a
 * consumer that calls `setPackAssetBase()` (template-monster) does so at
 * render time, after this module has already been evaluated, so the base
 * must be read on every lookup rather than once at import.
 */
const sceneBg = (id: SceneId) => packAsset(SCENE_BG[id]);

/** A dashed door knob behind the hanger's die-cut hole — where it hangs. */
function KnobSchematic() {
  const cx = HANGER_W / 2;
  const cy = 1.05 * 60; // the hole's centre, in the hanger's own pixels
  return (
    <svg
      className="pointer-events-none absolute"
      style={{ left: cx - 60, top: cy - 60 }}
      width={120}
      height={120}
      viewBox="0 0 120 120"
    >
      {/* knob face, larger than the hole — it sits behind the paper */}
      <circle cx={60} cy={60} r={47} fill="none" stroke={SCHEMA_INK} strokeWidth={1.5} strokeDasharray="5 4" />
      {/* the neck the hanger actually rests on */}
      <circle cx={60} cy={60} r={16} fill="none" stroke={SCHEMA_INK} strokeWidth={1.2} strokeDasharray="3 3" />
    </svg>
  );
}

/** Dashed H-stakes and a ground line under the yard sign. */
function StakesSchematic() {
  const drop = 78; // stake length below the sign, in sign pixels
  const w = SIGN_W;
  return (
    <svg
      className="pointer-events-none absolute"
      style={{ left: -36, top: SIGN_H - 4 }}
      width={w + 72}
      height={drop + 26}
      viewBox={`0 0 ${w + 72} ${drop + 26}`}
    >
      {[0.24, 0.76].map(f => (
        <g key={f}>
          <line
            x1={36 + w * f}
            y1={4}
            x2={36 + w * f}
            y2={4 + drop}
            stroke={SCHEMA_INK}
            strokeWidth={1.5}
            strokeDasharray="6 4"
          />
          {/* the H-stake's crossbar */}
          <line
            x1={36 + w * f - 14}
            y1={4 + drop * 0.45}
            x2={36 + w * f + 14}
            y2={4 + drop * 0.45}
            stroke={SCHEMA_INK}
            strokeWidth={1.2}
            strokeDasharray="4 3"
          />
        </g>
      ))}
      {/* ground */}
      <line x1={0} y1={4 + drop} x2={w + 72} y2={4 + drop} stroke={SCHEMA_INK} strokeWidth={1.5} strokeDasharray="7 5" />
      {Array.from({ length: 9 }, (_, i) => {
        const x = 30 + i * ((w + 12) / 8);
        return (
          <line
            key={i}
            x1={x}
            y1={4 + drop}
            x2={x - 12}
            y2={4 + drop + 12}
            stroke={SCHEMA_INK}
            strokeWidth={1}
          />
        );
      })}
    </svg>
  );
}

/**
 * A dashed van door around the magnet — door seams either side, the handle
 * above, the rocker line and a wheel arch below. Offsets are in the magnet's
 * own pixels; the SVG hangs past the piece and the stage crops what it must.
 */
function VanDoorSchematic() {
  // The magnet's box inside this SVG's viewBox
  const ox = 90;
  const oy = 70;
  const w = MAGNET_W + 180;
  const h = MAGNET_H + 172;
  return (
    <svg
      className="pointer-events-none absolute"
      style={{ left: -ox, top: -oy }}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
    >
      {/* door seams */}
      <line x1={ox - 46} y1={16} x2={ox - 46} y2={oy + MAGNET_H + 66} stroke={SCHEMA_INK} strokeWidth={1.5} strokeDasharray="7 5" />
      <line x1={ox + MAGNET_W + 46} y1={16} x2={ox + MAGNET_W + 46} y2={oy + MAGNET_H + 66} stroke={SCHEMA_INK} strokeWidth={1.5} strokeDasharray="7 5" />
      {/* handle */}
      <rect x={ox + MAGNET_W - 76} y={oy - 38} width={62} height={13} rx={6} fill="none" stroke={SCHEMA_INK} strokeWidth={1.2} strokeDasharray="4 3" />
      {/* rocker line with the rear wheel arch */}
      <path
        d={`M 0 ${oy + MAGNET_H + 66} H ${ox + 40} A 74 74 0 0 1 ${ox + 188} ${oy + MAGNET_H + 66} H ${w}`}
        fill="none"
        stroke={SCHEMA_INK}
        strokeWidth={1.5}
        strokeDasharray="7 5"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* The flipping card                                                   */
/* ------------------------------------------------------------------ */

function FlipCard({
  b,
  site,
  shared,
  reducedMotion,
  hoverless,
}: {
  b: PackBusiness;
  site: string;
  shared: { guides: boolean; nameMode: 'auto'; logoMode: 'auto' };
  reducedMotion: boolean;
  hoverless: boolean;
}) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      style={{ width: CARD_W, height: CARD_H, perspective: 1200 }}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
      onClick={() => setFlipped(f => !f)}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
          transition: reducedMotion ? undefined : 'transform 0.55s cubic-bezier(0.3,0.9,0.3,1)',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden' }}>
          <CardFront b={b} {...shared} layout="center" />
        </div>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <CardBack b={b} {...shared} layout="center" qrUrl={`${site}/review`} />
        </div>
      </div>
      <div className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400">
        {hoverless ? 'tap' : 'hover'} — the back
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

function Piece({
  spec,
  index,
  settled,
  reducedMotion,
  children,
}: {
  spec: PieceSpec;
  /** Position in the scene — staggers the settle-in. */
  index: number;
  settled: boolean;
  reducedMotion: boolean;
  children: React.ReactNode;
}) {
  const { x, y, z, rotate, scale, float } = spec;
  const { w, h } = PIECE_SIZE[spec.kind];
  // Shallow depth of field: the surface is out of focus, the card is sharp.
  const dof = Math.max(0, (84 - z) * 0.008);
  return (
    <div
      className="absolute"
      style={{
        left: x,
        top: y,
        width: w * scale,
        height: h * scale,
        transform: settled
          ? `translateZ(${z}px) rotate(${rotate}deg)`
          : `translateZ(${z + 140}px) rotate(${rotate * 2.5}deg)`,
        opacity: settled ? 1 : 0,
        transition: reducedMotion
          ? undefined
          : `transform 0.8s cubic-bezier(0.22,0.9,0.3,1) ${index * 0.09}s, opacity 0.45s ease ${index * 0.09}s`,
        transformStyle: 'preserve-3d',
        filter:
          `drop-shadow(${6 + z * 0.18}px ${10 + z * 0.3}px ${14 + z * 0.3}px rgba(30,34,40,${0.11 + z * 0.0008}))` +
          (dof > 0.05 ? ` blur(${dof.toFixed(2)}px)` : ''),
      }}
    >
      {/* Shadow, paper and edge share the float container — a bobbing piece
          must carry its whole silhouette or the bottom visibly detaches. */}
      <div
        className="relative"
        style={
          float && !reducedMotion
            ? { animation: `pack-piece-float ${float}s ease-in-out infinite` }
            : undefined
        }
      >
        {/* Contact shadow — the darkness right where paper meets table. */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: '5%',
            right: '5%',
            bottom: -9,
            height: 18,
            background:
              'radial-gradient(50% 50% at 50% 50%, rgba(30,34,40,0.26), transparent 72%)',
            filter: 'blur(5px)',
          }}
        />
        <div className="relative origin-top-left" style={{ transform: `scale(${scale})`, width: w }}>
          {children}
        </div>
        {/* A sliver of lit paper edge — card stock has thickness. */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 1,
            right: 1,
            bottom: 0,
            height: 1.5,
            borderRadius: 1,
            background: 'rgba(255,255,255,0.5)',
          }}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The shot                                                            */
/* ------------------------------------------------------------------ */

export function PackShowcase({
  b,
  domain,
  tunePlate = false,
  plateIdOverride,
  sceneOverride,
}: {
  b: PackBusiness;
  /** The live site — the hanger/sign QRs point at it, the card back at /review. */
  domain: string;
  /** Lab-only: the photo plate's geometry editor. */
  tunePlate?: boolean;
  /** Lab-only: force a specific plate instead of the one the trade maps to. */
  plateIdOverride?: string;
  /**
   * Force a scene instead of the trade's own. The invite uses `duo` for the
   * physical pack — the two pieces that actually ship — while the trade scenes
   * keep illustrating the print-ready PDF kit.
   */
  sceneOverride?: SceneId;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const [avail, setAvail] = useState(0);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const reducedMotion = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );
  /** No hover — a phone or tablet. Idle drift replaces the pointer tilt. */
  const hoverless = useMemo(() => window.matchMedia('(hover: none)').matches, []);
  const [settled, setSettled] = useState(reducedMotion);

  // Layout effect: the width must be known before the first paint, or the
  // showcase paints one zero-height frame and the plate arriving shifts
  // everything under it (the invite's CLS budget is zero).
  useLayoutEffect(() => {
    const node = host.current;
    if (!node) return;
    const sync = () => setAvail(node.getBoundingClientRect().width);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // The pieces settle onto the table the first time the scene is seen.
  useEffect(() => {
    if (reducedMotion || settled) return;
    const node = host.current;
    if (!node) return;
    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setSettled(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [reducedMotion, settled]);

  const narrow = avail > 0 && avail < NARROW_AT;
  const canvasW = narrow ? MOBILE_W : SCENE_W;
  const canvasH = narrow ? MOBILE_H : SCENE_H;
  const outerScale = avail > 0 ? Math.min(1, avail / canvasW) : 1;
  const site = `https://${domain}`;
  const sceneId = sceneOverride ?? sceneForTrade(b.trade);

  // The table photo fades in only once it has actually loaded.
  const [bgReady, setBgReady] = useState(false);
  useEffect(() => {
    setBgReady(false);
    const img = new Image();
    img.onload = () => setBgReady(true);
    img.src = sceneBg(sceneId);
  }, [sceneId]);

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reducedMotion || hoverless) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: py * -5, y: px * 7 });
  };

  // 'auto': a wordmark logo already carries the name — printing it again under
  // the mark reads as a mistake at mockup size.
  const shared = { guides: false, nameMode: 'auto' as const, logoMode: 'auto' as const };

  const renderPiece = (kind: PieceKind) => {
    switch (kind) {
      case 'sign':
        return (
          <>
            <YardSign b={b} {...shared} qrUrl={site} />
            <StakesSchematic />
          </>
        );
      case 'magnet':
        return (
          <>
            <VanDoorSchematic />
            <CarMagnet b={b} {...shared} />
          </>
        );
      case 'hanger':
        return (
          <>
            <DoorHanger b={b} {...shared} qrUrl={site} />
            <KnobSchematic />
          </>
        );
      case 'card':
        // Keyed so the flip state resets when the lab switches businesses.
        return (
          <FlipCard
            key={b.id}
            b={b}
            site={site}
            shared={shared}
            reducedMotion={reducedMotion}
            hoverless={hoverless}
          />
        );
    }
  };

  const scene = (narrow ? MOBILE_COMPOSITIONS : COMPOSITIONS)[sceneId];
  const drifting = hoverless && !reducedMotion;

  // Wide containers get the photo plate; phones keep the CSS composition, and
  // a plate that fails to load drops back to the CSS scene too. The duo scene
  // maps to its own partial plate — cards and magnet only.
  const [plateFailed, setPlateFailed] = useState(false);
  if (!narrow && !plateFailed) {
    return (
      <div ref={host} className="w-full">
        {avail > 0 ? (
          <PackPlate
            b={b}
            domain={domain}
            avail={avail}
            onFail={() => setPlateFailed(true)}
            tune={tunePlate}
            plateIdOverride={plateIdOverride ?? (sceneId === 'duo' ? 'duo' : undefined)}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div ref={host} className="w-full">
      {/* overflowX is a backstop, not the fix — transform-origin: top left (via
          the origin-top-left utility below; @foundlio/pack is now in template-
          monster's Tailwind content globs, fnd-hz8) keeps the scaled canvas
          within its own footprint; this just clips the rare piece whose
          drop-shadow/translateZ pokes past the canvas edge instead of letting
          it widen the page. overflowY stays visible so a floating piece's bob
          animation isn't clipped vertically. */}
      <div style={{ height: canvasH * outerScale, overflowX: 'clip', overflowY: 'visible' }}>
        <div
          className="origin-top-left"
          style={{ transform: `scale(${outerScale})`, width: canvasW }}
        >
          {/* The table. Perspective lives here so children can translateZ. */}
          <div
            className="relative overflow-hidden rounded-2xl ring-1 ring-zinc-200"
            style={{
              width: canvasW,
              height: canvasH,
              background:
                `radial-gradient(120% 90% at 70% 0%, ${b.brand.primary}14, transparent 55%),` +
                'linear-gradient(160deg, #faf9f6 0%, #f2efe8 60%, #eae6dd 100%)',
              perspective: 1600,
            }}
            onMouseMove={onMove}
            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
          >
            {/* The table photo, then a paper wash so the live print pieces stay
                the brightest thing on it. */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage: `url(${sceneBg(sceneId)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: bgReady ? 1 : 0,
                transition: 'opacity 0.6s ease',
              }}
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background: 'rgba(250,249,246,0.42)',
                opacity: bgReady ? 1 : 0,
                transition: 'opacity 0.6s ease',
              }}
            />
            {/* Faint grid — reads as a cutting mat while the photo loads. */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(30,34,40,0.045) 1px, transparent 1px),' +
                  'linear-gradient(90deg, rgba(30,34,40,0.045) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
                opacity: bgReady ? 0 : 1,
                transition: 'opacity 0.6s ease',
              }}
            />
            {/* Key light falling from the top-left, before anything sits down. */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(60% 50% at 22% 0%, rgba(255,255,255,0.55), transparent 65%)',
              }}
            />
            <div
              className={`absolute inset-0${drifting ? ' pack-scene-drift' : ''}`}
              style={{
                transform: drifting
                  ? undefined
                  : `rotateX(${9 + tilt.x}deg) rotateY(${tilt.y}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.25s ease-out',
              }}
            >
              {scene.map((spec, i) => (
                <Piece
                  key={spec.kind}
                  spec={spec}
                  index={i}
                  settled={settled}
                  reducedMotion={reducedMotion}
                >
                  {renderPiece(spec.kind)}
                </Piece>
              ))}
            </div>
            {/* Camera: vignette, then film grain over the whole shot. */}
            <div
              className="pointer-events-none absolute inset-0 z-10"
              style={{
                background:
                  'radial-gradient(120% 100% at 50% 40%, transparent 58%, rgba(30,34,40,0.13) 100%)',
              }}
            />
            <div
              className="pointer-events-none absolute inset-0 z-10"
              style={{ backgroundImage: GRAIN, backgroundSize: '180px 180px', opacity: 0.45, mixBlendMode: 'soft-light' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
